'use client';
import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import {
  PrismicRichText,
  SliceComponentProps,
  JSXMapSerializer,
} from '@prismicio/react';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback } from 'react';
import { LeftArrow } from '@/assets/LeftArrow';
import { RightArrow } from '@/assets/RightArrow';
import { ArrowRightIcon } from '@/assets';
import { StarIcon } from '@/assets/StarIcon';
import PlayIcon from '@/assets/PlayIcon';
import { HalfStarIcon } from '@/assets/logos/HalfStar';
import { Simplify } from '../../../prismicio-types';

const components: JSXMapSerializer = {
  heading2: ({ children }) => (
    <h2 className="text-4xl md:text-5xl font-bold text-white">{children}</h2>
  ),
  paragraph: ({ children }) => (
    <p className="text-lg text-white/80 max-w-2xl">{children}</p>
  ),
};

export type TestimonialCarouselProps =
  SliceComponentProps<Content.TestimonialCarouselSlice>;

const TestimonialCarousel = ({
  slice,
}: TestimonialCarouselProps): JSX.Element => {
  const testimonials = slice.primary?.testimonials || [];

  // TODO: Add new variation
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: !!testimonials && testimonials.length > 1,
    dragFree: true,
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const renderStars = (
    rating: number,
    filledColor: string = 'currentColor',
  ) => {
    const numericRating = parseFloat(String(rating)) || 0; // Ensure it's a number, default to 0
    const fullStars = Math.floor(numericRating);
    const hasHalfStar = numericRating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    const stars = [];

    // Render full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={`full-${i}`} fill={filledColor} />);
    }

    // Render half star if applicable with a gradient
    if (hasHalfStar) {
      // Generate a unique ID for the gradient to avoid conflicts if multiple carousels exist

      stars.push(<HalfStarIcon key={`half-${0}`} fill={filledColor} />);
    }

    // Render empty stars
    for (let i = 0; i < emptyStars; i++) {
      // Assuming StarIcon can take 'fill="none"' for an outlined star
      stars.push(<StarIcon key={`empty-${i}`} fill="#FFFFFF" />);
    }

    return stars;
  };

  if (!testimonials || testimonials.length === 0) {
    return <div></div>;
  }

  if (slice.variation === 'summarized') {
    const summarizedTestimonials =
      testimonials as Simplify<Content.TestimonialCarouselSliceSummarizedPrimaryTestimonialsItem>[];

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative pb-16 md:pb-32 text-white bg-[#0F423A]"
      >
        {/* Top Gradient Fade */}
        <div className="absolute top-0 left-0 w-full h-52 md:h-52 z-20 bg-gradient-to-b from-[#0E1010] to-transparent pointer-events-none" />

        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
            <div className="flex flex-col gap-4 mt-12 md:mt-18 z-20">
              <PrismicRichText
                field={slice.primary.heading}
                components={{
                  heading3: ({ children }) => (
                    <h3 className="text-4xl md:text-5xl font-bold">
                      {children}
                    </h3>
                  ),
                }}
              />
              <PrismicRichText
                field={slice.primary.subheading}
                components={components}
              />
              <PrismicNextLink
                field={slice.primary.cta_link}
                className="secondary-button mt-4 self-start"
              >
                <ArrowRightIcon className="arrow-icon" />
                {slice.primary.cta_link?.text ||
                  'See What Clients Say About Us'}
              </PrismicNextLink>
            </div>
            {slice.primary.testimonials.length > 3 && (
              <div className="flex gap-4 self-start md:self-end">
                <button
                  onClick={scrollPrev}
                  className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors"
                  aria-label="Previous testimonial"
                >
                  <LeftArrow />
                </button>
                <button
                  onClick={scrollNext}
                  className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors"
                  aria-label="Next testimonial"
                >
                  <RightArrow />
                </button>
              </div>
            )}
          </div>

          <div className="overflow-hidden mt-10" ref={emblaRef}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {summarizedTestimonials.map((item, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 min-w-full sm:min-w-1/2 lg:min-w-1/3 box-border"
                >
                  <div className="p-4 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm h-full flex flex-col">
                    {/* Right side: Content */}
                    <div className="w-full pt-8 pb-8 pr-8 flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-32 flex items-center ml-[33px]">
                          <PrismicNextImage
                            field={item.company_logo}
                            className="h-8 object-contain object-left"
                            fallbackAlt=""
                          />
                        </div>
                        <PrismicNextLink
                          field={item.testimonial_cta}
                          aria-label={`Read full case study for ${item.company_summary || 'this client'}`}
                        >
                          <RightArrow stroke="#34D399" />
                          <span className="sr-only">Read full case study</span>
                        </PrismicNextLink>
                      </div>
                      <div className="flex-grow my-4">
                        <p className="pl-8 text-white font-bold">
                          {item.company_summary}
                        </p>
                      </div>
                      <div className="pl-8 flex mb-2 gap-4">
                        {renderStars(item.star_rating || 0, '#FBBE36')}
                      </div>
                      <div className="flex-grow my-4">
                        <PrismicRichText
                          field={item.testimonial_text}
                          components={{
                            paragraph: ({ children }) => (
                              <p className="pl-8 text-white/80 italic">
                                &ldquo;{children}&rdquo;
                              </p>
                            ),
                          }}
                        />
                      </div>
                      <div className="pl-8 mt-auto pt-4">
                        <p className="text-white">{item.reviewer_name}</p>
                        <p className="text-white/60">{item.reviewer_role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  type DefaultTestimonnials =
    Simplify<Content.TestimonialCarouselSliceDefaultPrimaryTestimonialsItem>;
  const defaultTestimonials = testimonials as DefaultTestimonnials[];
  const firstDefaultTestimonial = defaultTestimonials[0];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pb-16 pt-24 text-white"
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
          <div className="flex flex-col gap-4">
            {firstDefaultTestimonial && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4">
                  <PrismicNextImage
                    field={firstDefaultTestimonial.review_platform_logo}
                    fallbackAlt=""
                  />
                  <div className="flex items-center gap-1">
                    <p className="font-bold text-white">
                      {firstDefaultTestimonial.star_rating}.0
                    </p>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <StarIcon key={i} />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-white/60">
                  {`Based on ${firstDefaultTestimonial.review_count} Clutch reviews`}
                </p>
              </div>
            )}
            <PrismicRichText
              field={slice.primary.heading}
              components={{
                heading3: ({ children }) => (
                  <h3 className="font-bold">{children}</h3>
                ),
              }}
            />
            <PrismicRichText
              field={slice.primary.subheading}
              components={components}
            />
            <PrismicNextLink
              field={slice.primary.cta_link}
              className="secondary-button mt-4 self-start"
            >
              <ArrowRightIcon className="arrow-icon" />
              View success stories
            </PrismicNextLink>
          </div>
          <div className="flex gap-4 self-start md:self-end">
            <button
              onClick={scrollPrev}
              className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors"
              aria-label="Previous testimonial"
            >
              <LeftArrow />
            </button>
            <button
              onClick={scrollNext}
              className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors"
              aria-label="Next testimonial"
            >
              <RightArrow />
            </button>
          </div>
        </div>

        <div className="overflow-hidden mt-16" ref={emblaRef}>
          <div className="flex">
            {defaultTestimonials?.map((item, index) => (
              <div key={index} className="flex-shrink-0 w-full p-4">
                <div className="bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm h-full flex flex-col md:flex-row overflow-hidden">
                  {/* Left side: Media */}
                  <div className="relative md:w-1/2 w-full aspect-video md:aspect-auto">
                    <PrismicNextImage
                      field={item.media}
                      className="w-full h-full object-cover"
                      fallbackAlt=""
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <PlayIcon />
                    </div>
                  </div>

                  {/* Right side: Content */}
                  <div className="md:w-1/2 w-full p-8 flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <PrismicNextImage
                        field={item.company_logo}
                        className="h-8 object-contain"
                        fallbackAlt=""
                      />
                      <PrismicNextLink
                        field={item.testimonial_cta}
                        aria-label={`Read full case study for ${item.reviewer_name || 'this client'}`}
                      >
                        <RightArrow stroke="#34D399" />
                        <span className="sr-only">Read full case study</span>
                      </PrismicNextLink>
                    </div>
                    <div className="flex mb-2">
                      {renderStars(item.star_rating || 0, '#FBBE36')}
                    </div>
                    <div className="flex-grow my-4">
                      <PrismicRichText
                        field={item.testimonial_text}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-white/80 italic">
                              &ldquo;{children}&rdquo;
                            </p>
                          ),
                        }}
                      />
                    </div>
                    <div className="mt-auto pt-4">
                      <p className="font-bold text-white">
                        {item.reviewer_name}
                      </p>
                      <p className="text-white/60">{item.reviewer_role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
